import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useShipToApi } from "@/hooks/apiHooks/useShipToApi";
import { useCategoryApi } from "@/hooks/apiHooks/useCategoryApi";
import { useProductApi } from "@/hooks/apiHooks/useProductApi";
import { usePromotionApi } from "@/hooks/apiHooks/usePromotionApi";
import { useClampPageToRange } from "@customer/_helpers/useClampPageToRange";
import type { GetAllProductsResponse } from "@models/product";
import type { GetHomePromotionsResponse } from "@models/promotion";
import { useListRead } from "./useListRead";

const PAGE_SIZE = 20;
// Single source of truth for how many popular categories/products the home page
// requests. BrowseHome also feeds this to the rows' skeleton placeholder counts
// so the loading state matches the real result count (no layout shift).
export const POPULAR_LIMIT = 8;

const EMPTY_RESPONSE: GetAllProductsResponse = {
    pageNumber: 1,
    totalPages: 0,
    count: 0,
    hasPreviousPage: false,
    hasNextPage: false,
    items: [],
};

// The promo banners' read (E14-S5). There is no "idle": the read is issued on
// mount, so the first render is already loading. `failed` stays distinct from
// a loaded answer with every slot empty, even though BrowseHome hides the
// banners for both. Not useListRead: that holds a list (T[], defaulting to
// []) and offers a Retry these banners never show; the answer here is one
// object of named slots.
type PromotionsRead =
    | { status: "loading" }
    | { status: "loaded"; promotions: GetHomePromotionsResponse }
    | { status: "failed" };

export const useBrowseHome = () => {
    const { callShipToApi } = useShipToApi();
    const { getPopular: getPopularCategories } = useCategoryApi();
    const { getPopular: getPopularProducts, getFeatured } = useProductApi();
    const { getHome: getHomePromotions } = usePromotionApi();
    // The "Featured Products" rail (#401 B3): the product each category
    // features, so it stops duplicating "Most Popular". Its failed state is
    // its own — see BrowseHome.
    const featured = useListRead(getFeatured);

    const [searchParams, setSearchParams] = useSearchParams();
    const pageNumber = Math.max(1, Number(searchParams.get("page")) || 1);

    // Popular categories/products come solely from the /popular endpoints. The
    // backend owns the ordering AND the fallback (pinned PopularRank first, then
    // 30-day order volume, then name), so there is no client-side fallback — the
    // rows render exactly what the API returns. They are empty only if the
    // catalog itself is empty, in which case empty carousels are correct. A
    // FAILED read is not that, so each row carries its own failed state, the
    // same as the Featured rail — and, as on the dedicated pages, that inline
    // state is the only message: the reads' toasts are suppressed.
    const readPopularCategories = useCallback(
        () =>
            getPopularCategories(POPULAR_LIMIT, {
                skipErrorNotification: true,
            }),
        [getPopularCategories],
    );
    const popularCategories = useListRead(readPopularCategories);

    const readPopularProducts = useCallback(
        () =>
            getPopularProducts(POPULAR_LIMIT, {
                skipErrorNotification: true,
            }),
        [getPopularProducts],
    );
    const popularProducts = useListRead(readPopularProducts);

    const [productPage, setProductPage] =
        useState<GetAllProductsResponse>(EMPTY_RESPONSE);
    const [isLoadingProducts, setIsLoadingProducts] = useState(true);
    // A failed grid read is its own state — "No products found" over a broken
    // read tells the shopper the catalogue is empty. Retry re-runs the effect.
    const [hasProductsError, setHasProductsError] = useState(false);
    const [productsAttempt, setProductsAttempt] = useState(0);

    const [promotionsRead, setPromotionsRead] = useState<PromotionsRead>({
        status: "loading",
    });

    // The banners are optional decoration, so a failed read hides them (see
    // BrowseHome) and must not toast either: the shopper did not ask for
    // them, and the rest of the page is unaffected.
    useEffect(() => {
        let cancelled = false;
        getHomePromotions({ skipErrorNotification: true })
            .then((promotions) => {
                if (!cancelled) setPromotionsRead({ status: "loaded", promotions });
            })
            .catch(() => {
                if (!cancelled) setPromotionsRead({ status: "failed" });
            });
        return () => {
            cancelled = true;
        };
    }, [getHomePromotions]);

    useEffect(() => {
        let cancelled = false;
        const fetchProducts = async () => {
            setIsLoadingProducts(true);
            setHasProductsError(false);
            try {
                const response = await callShipToApi<GetAllProductsResponse>(
                    `/products?pageNumber=${pageNumber}&pageSize=${PAGE_SIZE}`,
                );
                if (!cancelled) setProductPage(response);
            } catch {
                // useApi already surfaces the toast; the page renders the
                // failed state (not an empty grid) and the stale page is
                // dropped so it cannot sit under it.
                if (!cancelled) {
                    setProductPage(EMPTY_RESPONSE);
                    setHasProductsError(true);
                }
            } finally {
                if (!cancelled) setIsLoadingProducts(false);
            }
        };
        void fetchProducts();
        return () => {
            cancelled = true;
        };
    }, [pageNumber, callShipToApi, productsAttempt]);

    const isClampPending = useClampPageToRange(
        pageNumber,
        productPage,
        isLoadingProducts,
    );

    const retryProducts = useCallback(() => {
        setProductsAttempt((attempt) => attempt + 1);
    }, []);

    const setPage = useCallback(
        (newPage: number) => {
            const next = new URLSearchParams(searchParams);
            if (newPage <= 1) {
                next.delete("page");
            } else {
                next.set("page", String(newPage));
            }
            setSearchParams(next, { replace: false });
            if (typeof window !== "undefined") {
                window.scrollTo({ top: 0, behavior: "smooth" });
            }
        },
        [searchParams, setSearchParams],
    );

    return {
        popularCategories: popularCategories.items,
        popularProducts: popularProducts.items,
        featuredProducts: featured.items,
        isLoadingFeatured: featured.status === "loading",
        hasFeaturedError: featured.status === "failed",
        retryFeatured: featured.retry,
        promotionsRead,
        productPage,
        // The clamp reads the raw flag above; the page shows the skeleton
        // while a clamp is pending too (PR 395 review F1).
        isLoadingProducts: isLoadingProducts || isClampPending,
        hasProductsError,
        retryProducts,
        isLoadingPopular: popularProducts.status === "loading",
        hasPopularError: popularProducts.status === "failed",
        retryPopular: popularProducts.retry,
        isLoadingCategories: popularCategories.status === "loading",
        hasCategoriesError: popularCategories.status === "failed",
        retryCategories: popularCategories.retry,
        pageNumber,
        setPage,
    };
};
